import { EyeIcon } from "lucide-react";
import { useEffect } from "react";
import type { SupportSession } from "@/api/support";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/domain/format/date";
import { msUntilExpiry } from "@/domain/platform/support";
import { useEndSupport } from "@/features/support/hooks/use-support";

/** Always on screen during a support session: whose data this is, that it is read-only, when it ends, and a way out. */
export function SupportBanner({ session }: { session: SupportSession }) {
  const end = useEndSupport();
  const { mutate } = end;

  // when the hour is up the database stops honoring the session, so leave the organization's app too
  useEffect(() => {
    const timer = window.setTimeout(() => mutate(), msUntilExpiry(session.expiresAt, new Date()));
    return () => window.clearTimeout(timer);
  }, [session.expiresAt, mutate]);

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-lg border border-warning/60 bg-warning px-4 py-2 text-sm text-warning-foreground shadow-lg"
    >
      <EyeIcon className="size-4 shrink-0" aria-hidden="true" />
      <span>
        <strong>Support mode</strong> · read-only · viewing {session.tenantName} · ends{" "}
        {formatDateTime(new Date(session.expiresAt))}
      </span>
      <Button size="sm" variant="outline" className="bg-background text-foreground" disabled={end.isPending} onClick={() => mutate()}>
        {end.isPending ? "Exiting…" : "Exit"}
      </Button>
    </div>
  );
}
